//
// MIDI-related functions
//
// MIDI channel and program numbers passed to functions are 1-based
//
// Copyright (c) 2019 Red Panda LLC.  All rights reserved.

// MIDI constants
const SYSEX_START = 0xF0;
const SYSEX_END   = 0xF7;

const RED_PANDA_ID_1 = 0x00;
const RED_PANDA_ID_2 = 0x02;
const RED_PANDA_ID_3 = 0x23;

// Message types
const MESSAGE_VERSION = 0x00;
const MSG_GET_PROPERTY = 0x34;
const MSG_REPLY_GET_PROPERTY = 0x35;
const MSG_SET_PROPERTY = 0x36;

// Properties
export const PROPERTY_FIRMWARE_VERSION	= 0x7F01;
export const PROPERTY_ERROR_CODE		= 0x7F0A;
export const PROPERTY_PSU_VOLTAGE		= 0x7F0B;
export const PROPERTY_MAX_INPUT_LEVEL	= 0x7F10;
export const PROPERTY_BYPASS_MODE		= 0x7F11;
export const PROPERTY_MIDI_CHANNEL		= 0x7F12;
export const PROPERTY_SAVE_PRESET		= 0x7F13;
export const PROPERTY_KILL_DRY			= 0x7F14;
export const PROPERTY_IO_CONFIG			= 0x7F15;
export const PROPERTY_TRAILS			= 0x7F16;
export const PROPERTY_RECEIVE_MIDI_CLOCK= 0x7F17;
export const PROPERTY_AUX_SWITCH_MODE	= 0x7F18;
export const PROPERTY_MAIN_SWITCH_MODE	= 0x7F19;
export const PROPERTY_PROGRAM_VALID		= 0x7F1A;	// bank, program
export const PROPERTY_PROGRAM_DATA		= 0x7F1B;	// bank, program
export const PROPERTY_PROGRAM_NAME		= 0x7F1C;	// bank, program
export const PROPERTY_EDIT_BUFFER_DATA	= 0x7F1D;
export const PROPERTY_PROG_LOAD_IGNORE_MIX	= 0x7F1E;
export const PROPERTY_PROG_DATA_LOCKED	= 0x7F1F;
export const PROPERTY_ACTIVE_SENSING	= 0x7F20;
export const PROPERTY_RECEIVE_MIDI_NOTE	= 0x7F21;

// control port properties
export const PROPERTY_CTRL_MODE			= 0x7E00;
export const PROPERTY_EXPR_DEST_1		= 0x7E04;
export const PROPERTY_EXPR_MIN_1		= 0x7E05;
export const PROPERTY_EXPR_MAX_1		= 0x7E06;
export const PROPERTY_EXPR_DEST_2		= 0x7E07;
export const PROPERTY_EXPR_MIN_2		= 0x7E08;
export const PROPERTY_EXPR_MAX_2		= 0x7E09;
export const PROPERTY_EXPR_DEST_3		= 0x7E0A;
export const PROPERTY_EXPR_MIN_3		= 0x7E0B;
export const PROPERTY_EXPR_MAX_3		= 0x7E0C;
export const PROPERTY_EXPR_DEST_4		= 0x7E0D;
export const PROPERTY_EXPR_MIN_4		= 0x7E0E;
export const PROPERTY_EXPR_MAX_4		= 0x7E0F;
export const PROPERTY_EXPR_DEST_5		= 0x7E10;
export const PROPERTY_EXPR_MIN_5		= 0x7E11;
export const PROPERTY_EXPR_MAX_5		= 0x7E12;
export const PROPERTY_EXPR_DEST_6		= 0x7E13;
export const PROPERTY_EXPR_MIN_6		= 0x7E14;
export const PROPERTY_EXPR_MAX_6		= 0x7E15;
export const PROPERTY_REMOTE_BANK		= 0x7E1C;
export const PROPERTY_CTRL_SAVE_CONFIG	= 0x7E3F;

var midiAPI = null;
var deviceOutput = null;
var deviceInput = null;
var midiChannel = 0;
var productFamilyId = 0;
var productId = 0;
var productName = 'None';
var logToConsole = false;

var currentTab = 'edit';

// Preset subscriber.  Listens to PROPERTY_PROGRAM_VALID, but handled
// differently because we need to pass data when retrieving the property value.
var programSubscriber = null;

// property (including parameters) value subscribers.  Key is property ID.
var propertyValueSubscribers = {};

// MIDI continuous controller value subscribers.  Key is CC number.
var midiCCSubscribers = {};

// Callback functions for getProgramData.  Key is program number (bank is always 0).
// Callbacks are deleted after they are called.
var programDataCallbacks = {};

// Callback functions for getProgramName.  Key is program number (bank is always 0).
// Callbacks are deleted after they are called.
var programNameCallbacks = {};

export function initialize() {
	if (navigator.requestMIDIAccess) {
		navigator.requestMIDIAccess({ sysex: true }).then(onMIDISuccess, onMIDIFailure);
	} else {
		alert('Web MIDI is not supported by this browser or connection.\nPlease use Chrome and verify that the URL starts with \"https\".');
	}
}

export function isLoggingEnabled() {
	return logToConsole;
}

export function setLogging(value) {
	logToConsole = value;
}

// Encodes 8-bit data into 7 bits for SysEx transmission.
// The output will always be a multiple of 4 bytes.
//
// data_8bit			8-bit input data.
//
// returns: 7-bit encoded data.
function  encodeSysExData(data_8bit)
{
	var data_7bit = [];
	var idx8 = 0;

	while (idx8 < data_8bit.length) {
		var packetBytes = Math.min(data_8bit.length - idx8, 3);

		// write in big endian order
		switch (packetBytes) {
			case 3:
				data_7bit.push(((data_8bit[idx8  ] & 0xE0) >> 5));
				data_7bit.push(((data_8bit[idx8  ] & 0x1F) << 2) | ((data_8bit[idx8+1] & 0xC0) >> 6));
				data_7bit.push(((data_8bit[idx8+1] & 0x3F) << 1) | ((data_8bit[idx8+2] & 0x80) >> 7));
				data_7bit.push(  data_8bit[idx8+2] & 0x7F);
				break;

			case 2:
				data_7bit.push(((data_8bit[idx8  ] & 0xE0) >> 5));
				data_7bit.push(((data_8bit[idx8  ] & 0x1F) << 2) | ((data_8bit[idx8+1] & 0xC0) >> 6));
				data_7bit.push(( data_8bit[idx8+1] & 0x3F) << 1);
				data_7bit.push(0);
				break;

			case 1:
				data_7bit.push(((data_8bit[idx8  ] & 0xE0) >> 5));
				data_7bit.push( (data_8bit[idx8  ] & 0x1F) << 2);
				data_7bit.push(0);
				data_7bit.push(0);
				break;
		}

		idx8 += packetBytes;
	}

	return data_7bit
}

// Decodes 7-bit SysEx data.
//
// data_7bit		7-bit input data.
// dataLength8bit	unencoded data length
//
// returns: 8-bit decoded data.
//
function decodeSysExData(data_7bit, dataLength8bit)
{
	var data_8bit = [];

	// input has to consist of full 4-byte packets
	if ((data_7bit.length % 4) != 0) {
		console.log("7-bit encoded data has invalid length: " + data_7bit.length);
		return data_8bit;
	}

	for (var idx7 = 0; idx7 < data_7bit.length; idx7 += 4) {
		if ((data_7bit[idx7  ] >= 0x80) ||
		    (data_7bit[idx7+1] >= 0x80) ||
		    (data_7bit[idx7+2] >= 0x80) ||
		    (data_7bit[idx7+3] >= 0x80)) {

			console.log("7-bit encoded data has invalid format");
			data_8bit = [];
			return data_8bit;
		}

		data_8bit.push(((data_7bit[idx7  ] & 0x07) << 5) | ((data_7bit[idx7+1] & 0x7C) >> 2));
		data_8bit.push(((data_7bit[idx7+1] & 0x03) << 6) | ((data_7bit[idx7+2] & 0x7E) >> 1));
		data_8bit.push(((data_7bit[idx7+2] & 0x01) << 7) | ((data_7bit[idx7+3] & 0x7F)     ));
	}

	// truncate array to decoded data length
	return data_8bit.slice(0, dataLength8bit);
}

function onMIDISuccess(midiAccess) {
	midiAPI = midiAccess;

	console.log(midiAccess);

	findMIDIDevice();
}

function onMIDIFailure(e) {
	console.log('Browser does not support Web MIDI, or no access to MIDI devices: ' + e);
}

function onMIDIMessage(event) {
	if ((event.data[0] & 0xF0) == 0xB0)
	{
		if ((event.data[0] & 0x0F) == midiChannel &&
			event.data.length == 3)
		{
			onMIDIControlChange(event.data[0] & 0x0F,
								event.data[1],
								event.data[2]);
		}
	}
	else if (event.data[0] == 0xF0) {
		onMIDISystemExclusive(event.data);
	}
}

function onMIDIControlChange(channel, controller, value) {
	if (logToConsole) {
		console.log('CC In: ch ' + channel + ' cc ' + controller + ' = ' + value);
	}

	var target = midiCCSubscribers[controller];
	if (target != undefined) {
		target.handleControlChange(controller, value);
	}
}

function onMIDISystemExclusive(data) {
	if (logToConsole) {
		var str = 'SysEx In: ';
		for (var i=0; i<data.length; i++) {
			str += "0x" + data[i].toString(16) + " ";
		}
		console.log( str );
	}

	var propertyId = (data[8] << 8) | data[9];
	var dataIdx = 10;

	if (data[0] == SYSEX_START &&
		data[1] == RED_PANDA_ID_1 &&
		data[2] == RED_PANDA_ID_2 &&
		data[3] == RED_PANDA_ID_3 &&
		data[4] == productFamilyId &&
		data[5] == productId &&
		data[6] == MESSAGE_VERSION)
	{
		var messageType = data[7];
		if (messageType == MSG_REPLY_GET_PROPERTY) {
			if (propertyId == PROPERTY_FIRMWARE_VERSION) {
				var subscriber = propertyValueSubscribers[propertyId];
				if (subscriber != undefined) {
					var major = data[dataIdx++];
					var minor = data[dataIdx++];
					var patch = data[dataIdx++];
					var releaseType = data[dataIdx++];
	
					var buildNum = data[dataIdx++] << 21;
					buildNum |= data[dataIdx++] << 14;
					buildNum |= data[dataIdx++] << 7;
					buildNum |= data[dataIdx++];
	
					var firmwareString = major + "." + minor + "." + patch + "." +
								String.fromCharCode(releaseType) +
								" build " + buildNum;

					subscriber.setText(firmwareString);
				}
			}
			else if (propertyId == PROPERTY_ERROR_CODE) {
				var subscriber = propertyValueSubscribers[propertyId];
				if (subscriber != undefined) {
					var error1 = data[dataIdx++] << 28;
					error1    |= data[dataIdx++] << 21;
					error1    |= data[dataIdx++] << 14;
					error1    |= data[dataIdx++] <<  7;
					error1    |= data[dataIdx++];
	
					var error2 = data[dataIdx++] << 28;
					error2    |= data[dataIdx++] << 21;
					error2    |= data[dataIdx++] << 14;
					error2    |= data[dataIdx++] <<  7;
					error2    |= data[dataIdx++];
					if (error2 < 0) {
						error2 = 0xFFFFFFFF + error2 + 1;
					}
	
					var errorStr = undefined;
				
					if (error1 != 0) {
						errorStr = error1.toString() + " (0x" + error2.toString(16).toUpperCase() + ")";
					}

					subscriber.setText(errorStr);
				}
			}
			else if (propertyId == PROPERTY_PSU_VOLTAGE) {
				var subscriber = propertyValueSubscribers[propertyId];
				if (subscriber != undefined) {
					var voltage = data[dataIdx++] << 7;
					voltage    |= data[dataIdx++];
					voltage    /= 100;

					subscriber.setValue(voltage);
				}
			}
			else if (propertyId == PROPERTY_MAX_INPUT_LEVEL) {
				var target = propertyValueSubscribers[propertyId];
				if (target != undefined) {
					target.setValue(data[dataIdx]);
				}
			}
			else if (propertyId == PROPERTY_BYPASS_MODE) {
				var target = propertyValueSubscribers[propertyId];
				if (target != undefined) {
					target.setValue(data[dataIdx]);
				}
			}
			else if (propertyId == PROPERTY_MIDI_CHANNEL) {
				var target = propertyValueSubscribers[propertyId];
				if (target != undefined) {
					var channel = data[dataIdx];
					target.setValue(channel);
					midiChannel = channel;
				}
			}
			else if (propertyId == PROPERTY_IO_CONFIG) {
				var target = propertyValueSubscribers[propertyId];
				if (target != undefined) {
					target.setValue(data[dataIdx]);
				}
			}
			else if (propertyId == PROPERTY_TRAILS) {
				var target = propertyValueSubscribers[propertyId];
				if (target != undefined) {
					target.setValue(data[dataIdx]);
				}
			}
			else if (propertyId == PROPERTY_RECEIVE_MIDI_CLOCK) {
				var target = propertyValueSubscribers[propertyId];
				if (target != undefined) {
					target.setValue(data[dataIdx]);
				}
			}
			else if (propertyId == PROPERTY_AUX_SWITCH_MODE) {
				var target = propertyValueSubscribers[propertyId];
				if (target != undefined) {
					target.setValue(data[dataIdx]);
				}
			}
			else if (propertyId == PROPERTY_MAIN_SWITCH_MODE) {
				var target = propertyValueSubscribers[propertyId];
				if (target != undefined) {
					target.setValue(data[dataIdx]);
				}
			}
			else if (propertyId == PROPERTY_CTRL_MODE) {
				var target = propertyValueSubscribers[propertyId];
				if (target != undefined) {
					target.setValue(data[dataIdx]);
				}
			}
			else if (propertyId == PROPERTY_REMOTE_BANK) {
				var target = propertyValueSubscribers[propertyId];
				if (target != undefined) {
					target.setValue(data[dataIdx]);
				}
			}
			else if (propertyId == PROPERTY_PROGRAM_VALID) {
				var bank = data[dataIdx++];
				var prog = data[dataIdx++];
				var valid = (data[dataIdx++] != 0);
				var target = programSubscriber;
				if (target != null) {
					target.notifyProgramValid(bank, prog, valid);
				}
			}
			else if (propertyId == PROPERTY_PROGRAM_NAME) {
				var bank = data[dataIdx++];
				var prog = data[dataIdx++];

				// length of name in UTF-8 bytes
				var nameLen = data[dataIdx++] << 7;
				nameLen     = nameLen | data[dataIdx++];

				var encodedLen = data.length - dataIdx - 1; // encoded name is remaining SysEx string, minus F7 terminator

				if (logToConsole) {
					console.log("Received program name for bank " + bank + " program " + prog + " (" + data.length + " bytes), name is " + nameLen + " bytes (" + encodedLen + " bytes encoded)");
					console.log(data);
				}

				if (nameLen > 0) {
					var name_7bit = new Uint8Array(encodedLen);
					for (var i = 0; i < encodedLen; i++) {
						name_7bit[i] = data[dataIdx++];
					}
	
					var name_8bit = decodeSysExData(name_7bit, nameLen);
	
					var nameBuffer = new Uint8Array(name_8bit.length);
					for (var i = 0; i < name_8bit.length; i++) {
						nameBuffer[i] = name_8bit[i];
					}
				
					var decoder = new TextDecoder();
					var name = decoder.decode(nameBuffer);
	
					if (logToConsole) {
						console.log("notifyProgramName(" + bank + ", " + prog + ", \"" + name + "\")");
					}
	
					if (bank == 0 && prog in programNameCallbacks) {
						programNameCallbacks[prog](name);
						delete programNameCallbacks[prog];
					} else if (logToConsole) {
						console.log("bank " + bank + " program " + prog + " name callback undefined.");
					}
				}
			}
			else if (propertyId == PROPERTY_PROG_LOAD_IGNORE_MIX) {
				var target = propertyValueSubscribers[propertyId];
				if (target != undefined) {
					target.setValue(data[dataIdx]);
				}
			}
			else if (propertyId == PROPERTY_PROG_DATA_LOCKED) {
				var target = propertyValueSubscribers[propertyId];
				if (target != undefined) {
					target.setValue(data[dataIdx]);
				}
			}
			else if (propertyId == PROPERTY_ACTIVE_SENSING) {
				var target = propertyValueSubscribers[propertyId];
				if (target != undefined) {
					target.setValue(data[dataIdx]);
				}
			}
			else if (propertyId == PROPERTY_RECEIVE_MIDI_NOTE) {
				var target = propertyValueSubscribers[propertyId];
				if (target != undefined) {
					target.setValue(data[dataIdx]);
				}
			}
			else {
				// assume control will handle it
				var iValue = data[dataIdx++] << 21;
				iValue |= data[dataIdx++] << 14;
				iValue |= data[dataIdx++] << 7;
				iValue |= data[dataIdx++];


				var subscriber = propertyValueSubscribers[propertyId];
				if (subscriber != undefined) {
					if ((propertyId & 0x2000) != 0) {
						// indexed parameter value
						subscriber.setValue(iValue);
					} else {
						// value in [0,1]
						subscriber.setValue(iValue / 8388608.0);
					}

					//subscriber.props.value = value;

//					if (node.nodeName == 'INPUT' && node.type == 'range') {
//						// slider
//						node.value = node.min + value * (node.max - node.min);
//					}
//					else if (node.nodeName == 'SELECT') {
//						var items = node.children;
//
//						var idx;
//						if (node.classList.contains('idxparam')) {
//							idx = iValue;
//						} else {
					//
//							idx = Math.max(0, Math.min(Math.trunc(value * items.length), items.length - 1));
//						}
//
//						node.selectedIndex = idx;
//					}
//					else if (node.nodeName == 'BTN-GROUP' && node.type == 'absolute') {
//						var items = node.children;
//						var idx;
//						if (node.classList.contains('idxparam')) {
//							idx = iValue;
//						} else {
//							idx = Math.max(0, Math.min(Math.trunc(value * items.length), items.length - 1));
//						}
//						for (var i = 0; i < items.length; i++) {
//							if (i == idx) {
//								items[i].classList.add('active')
//							} else {
//								items[i].classList.remove('active')
//							}
//						}
//					}
				}
			}
		}
		else if (messageType == MSG_SET_PROPERTY) {
			// Program data is returned as a set property message, so
			// if can be stored and sent back to the pedal to restore
			// the program.
			if (propertyId == PROPERTY_PROGRAM_DATA) {
				// callback was registered by getProgramData
				var bank = data[dataIdx++]
				var prog = data[dataIdx++]

				console.log("Received bank " + bank + " program " + prog + " (" + data.length + " bytes)");

				if (bank == 0 && prog in programDataCallbacks) {
					programDataCallbacks[prog](data);
					delete programDataCallbacks[prog];
				} else {
					console.log("bank " + bank + " program " + prog + " data callback undefined.");
				}
			}
		}
	}
}

export function getMIDIInputs() {
	return midiAPI ? midiAPI.inputs : null;
}

export function getMIDIOutputs() {
	return midiAPI ? midiAPI.outputs : null;
}

export function findMIDIDevice() {
	deviceInput = null;
	deviceOutput = null;

	// find connected output devices
	var outputs = midiAPI.outputs.values();
	for (var output = outputs.next(); output && !output.done; output = outputs.next()) {
		var device = output.value;
		console.log(device.name);

		if (device.name.startsWith(productName)) {
			deviceOutput = output.value;
			console.log('Found output device.');
		}
	}

	// find connected input devices
	var inputs = midiAPI.inputs.values();
	for (var input = inputs.next(); input && !input.done; input = inputs.next()) {
		var device = input.value;
		if (device.name.startsWith(productName)) {
			deviceInput = input.value;
			console.log('Found input device.');
			deviceInput.onmidimessage = onMIDIMessage;
		}
	}

	if (deviceOutput) {
		setDeviceStatus('Connected');
		onDeviceConnected();
	} else {
		setDeviceStatus('Not found');
	}

	if (!deviceInput) {
		console.log('input device not found.');
	}

	if (!deviceOutput) {
		console.log('output device not found.');
	}
}

// called during startup
export function setProductId(familyId, pid, name) {
	productFamilyId = familyId;
	productId = pid;
	productName = name;
	console.log(productName + ' (' + productFamilyId + ':' + productId + ')');
}

function setDeviceStatus(status) {
	var node = document.getElementById("device-status");
	if (node) {
		node.innerHTML = status;
	}
}

function onDeviceConnected() {
	updateAllPropertyValues();

	// Update preset table
	// This assumes 127 programs, but will ignore 
	updateProgramsValid(0, 128);

	// we do not need to call midi.updateProgramNames( 0, this.numProgs),
	// because PresetTable will ask for the program name when we get notification
	// for each valid program.
}

function getDeviceProperty(propertyId, data=[], logSysEx = false) {
	if (deviceOutput) {
		var message = [ SYSEX_START, RED_PANDA_ID_1, RED_PANDA_ID_2, RED_PANDA_ID_3 ];
		message.push(productFamilyId);
		message.push(productId);
	
		// message type
		message.push(MESSAGE_VERSION);
		message.push(MSG_GET_PROPERTY);
	
		// property ID
		message.push((propertyId >> 8) & 0x7F);
		message.push(propertyId & 0x007F);

		for (var i = 0; i < data.length; i++) {
			message.push(data[i]);
		}
	
		message.push(SYSEX_END);
	
		if (logSysEx || logToConsole) {
			console.log(message);
		}

		deviceOutput.send(message);
	}
}

export function setDeviceProperty(propertyId, dataBytes) {
	var message = [ SYSEX_START, RED_PANDA_ID_1, RED_PANDA_ID_2, RED_PANDA_ID_3 ];
	message.push(productFamilyId);
	message.push(productId);

	// message type
	message.push(MESSAGE_VERSION);
	message.push(MSG_SET_PROPERTY);

	// property ID
	message.push((propertyId >> 8) & 0x7F);
	message.push(propertyId & 0x007F);

	// data bytes
	for (var i = 0; i < dataBytes.length; i++) {
		message.push(dataBytes[i]);
	}

	// end
	message.push(SYSEX_END);

	if (logToConsole) {
		console.log(message);
	}

	if (deviceOutput) {
		deviceOutput.send(message);
	}
}

/*
 * Asks the pedal if a program has valid data.  The
 * response.  The response will come asynchronously
 * and be forwarded to PROPERTY_PROGRAM_VALID subscribers.
 *
 * bank - MIDI bank number.
 * program - MIDI program number.
 * logSysEx - true to log outgoing SysEx message to console.
 */
export function getIsProgramValid(bank, program, logSysEx = false) {
	if (deviceOutput) {
		var message = [ SYSEX_START, RED_PANDA_ID_1, RED_PANDA_ID_2, RED_PANDA_ID_3 ];
		message.push(productFamilyId);
		message.push(productId);
	
		// message type
		message.push(MESSAGE_VERSION);
		message.push(MSG_GET_PROPERTY);
	
		// property ID
		message.push((PROPERTY_PROGRAM_VALID >> 8) & 0x7F);
		message.push(PROPERTY_PROGRAM_VALID & 0x007F);

		// address
		message.push(bank);
		message.push(program);
	
		message.push(SYSEX_END);
	
		if (logSysEx || logToConsole) {
			console.log(message);
		}

		deviceOutput.send(message);
	}
}

/*
 * Downloads program data and returns a string containing the bytes.
 *
 * bank - MIDI bank number.
 * program - MIDI program number.
 * callback - function to be called when data is received.
 * logSysEx - true to log outgoing SysEx message to console.
 */
export function getProgramData(bank, program, callback, logSysEx = false) {
	if (deviceOutput) {
		programDataCallbacks[program] = callback;

		var message = [ SYSEX_START, RED_PANDA_ID_1, RED_PANDA_ID_2, RED_PANDA_ID_3 ];
		message.push(productFamilyId);
		message.push(productId);
	
		// message type
		message.push(MESSAGE_VERSION);
		message.push(MSG_GET_PROPERTY);
	
		// property ID
		message.push((PROPERTY_PROGRAM_DATA >> 8) & 0x7F);
		message.push(PROPERTY_PROGRAM_DATA & 0x007F);

		// address
		message.push(bank);
		message.push(program);
	
		message.push(SYSEX_END);
	
		if (logSysEx || logToConsole) {
			console.log(message);
		}

		deviceOutput.send(message);
	}
}

/*
 * Uploads program data to the pedal.
 *
 * bank - MIDI bank number.
 * program - MIDI program number.
 * data - program data.
 */
export function setProgramData(bank, program, data) {
	if (deviceOutput && bank >= 0 && bank < 128 && program >= 0 && program < 128) {
		if (data[0] == SYSEX_START) {
			// SysEx message
			if (data[1] == RED_PANDA_ID_1 &&
				data[2] == RED_PANDA_ID_2 &&
				data[3] == RED_PANDA_ID_3 &&
				data[4] == productFamilyId &&
				data[5] == productId &&
				data[6] == MESSAGE_VERSION &&
				data[7] == MSG_SET_PROPERTY) {

				if (data[8] == ((PROPERTY_PROGRAM_DATA >> 8) & 0x7F) &&
					data[9] ==  (PROPERTY_PROGRAM_DATA & 0x007F)) {

					// Override the bank and program number, so that
					// we can load the preset in a different location
					// than where it was saved from.
					data[10] = bank;
					data[11] = program;

					deviceOutput.send(data);
				} else {
					console.log("SysEx does not contain program data");
				}
			} else {
				console.log("Invalid SysEx company ID, product ID, message version or type");
			}
		} else {
			console.log("Not valid SysEx");
		}
	}
}

/*
 * Retrieves a program name from the pedal and returns a string.
 *
 * bank - MIDI bank number.
 * program - MIDI program number.
 * callback - function to be called when data is received.
 * logSysEx - true to log outgoing SysEx message to console.
 */
export function getProgramName(bank, program, callback, logSysEx = false) {
	if (deviceOutput) {
		programNameCallbacks[program] = callback;

		var message = [ SYSEX_START, RED_PANDA_ID_1, RED_PANDA_ID_2, RED_PANDA_ID_3 ];
		message.push(productFamilyId);
		message.push(productId);
	
		// message type
		message.push(MESSAGE_VERSION);
		message.push(MSG_GET_PROPERTY);
	
		// property ID
		message.push((PROPERTY_PROGRAM_NAME >> 8) & 0x7F);
		message.push(PROPERTY_PROGRAM_NAME & 0x007F);

		// address
		message.push(bank);
		message.push(program);
	
		message.push(SYSEX_END);
	
		if (logSysEx || logToConsole) {
			console.log(message);
		}

		deviceOutput.send(message);
	}
}

/*
 * Changes a preset name on the pedal.
 *
 * bank - MIDI bank number.
 * program - MIDI program number.
 * name - new program name.
 */
export function setProgramName(bank, program, name) {
	if (logToConsole) {
		console.log("setProgramName(" + bank + ", " + program + ", \"" + name + "\")");
	}

	if (deviceOutput && bank >= 0 && bank < 128 && program >= 0 && program < 128) {
		var data = [ bank, program ];

		var encoder = new TextEncoder();
		var name_8bit = encoder.encode(name);

		// 2 byte length before encoding
		data.push((name_8bit.length >> 7) & 0x7F);
		data.push( name_8bit.length       & 0x7F);

		var name_7bit = encodeSysExData(name_8bit);
		for (var i = 0; i < name_7bit.length; i++) {
			data.push(name_7bit[i]);
		}
		
		setDeviceProperty(PROPERTY_PROGRAM_NAME, data);
	}
}

// deprecated
function sendSysExCmd(command, dataBytes) {
	var message = [ SYSEX_START, RED_PANDA_ID_1, RED_PANDA_ID_2, RED_PANDA_ID_3 ];
	message.push(productId);

	// command
	message.push(command);

	// data bytes
	for (var i = 0; i < dataBytes.length; i++) {
		message.push(dataBytes[i]);
	}

	message.push(SYSEX_END);

	if (logToConsole) {
		console.log(message);
	}

	if (deviceOutput) {
		deviceOutput.send(message);
	}
}

function setMaxInputLevel(x) {
	if (logToConsole) {
		console.log('Max input level: ' + x);
	}

	setDeviceProperty(PROPERTY_MAX_INPUT_LEVEL, [x]);
}

export function setMIDIChannel(ch) {
	if (logToConsole) {
		console.log('MIDI Channel: ' + ch);
	}

	setDeviceProperty(PROPERTY_MIDI_CHANNEL, [ch]);
	midiChannel = ch;
}

function setKillDry(kill) {
	if (logToConsole) {
		console.log('Kill dry: ' + kill);
	}

	setDeviceProperty(PROPERTY_KILL_DRY, [kill]);
}

function setIOConfig(io) {
	if (logToConsole) {
		console.log('I/O config: ' + io);
	}

	setDeviceProperty(PROPERTY_IO_CONFIG, [io]);
}

// Gets PROPERTY_PROGRAM_VALID from pedal for
// all program numbers in the range [start,end).
//
// start - first program to check
// end - past-the-end program
export function updateProgramsValid(start, end) {
	const period = 50; // spread out updates
	var t = 50;

	const bank = 0;
	for (var i = start; i < end; i++) {
		// create an anonymous function to retrieve each property
		setTimeout((function(prog) {
			return function() {
				getDeviceProperty(PROPERTY_PROGRAM_VALID, [bank, prog]);
			}
		})(i), t += period);
	}
}

// Gets PROPERTY_PROGRAM_NAME from pedal for
// all program numbers in the range [start,end).
//
// start - first program to check
// end - past-the-end program
export function updateProgramNames(start, end) {
	const period = 50; // spread out updates
	var t = 50;

	const bank = 0;
	for (var i = start; i < end; i++) {
		// create an anonymous function to retrieve each property
		setTimeout((function(prog) {
			return function() {
				getDeviceProperty(PROPERTY_PROGRAM_NAME, [bank, prog]);
			}
		})(i), t += period);
	}
}

export function updateAllPropertyValues() {
	// spread out the property updates 
	const period = 10;
	var t = 10;

	for (var propId in propertyValueSubscribers) {
		// create an anonymous function to save the propId for each call
		setTimeout((function(pid) {
			return function() {
				getPropertyValue(pid);
			}
		})(propId), t += period);
	} }

export function getPropertyValue(propId) {
	getDeviceProperty(propId);
}

export function setParameterValue(paramId, value) {
	var iValue = Math.trunc(8388608 * value);
	var data = [ (iValue >> 21) & 0x7F,
				 (iValue >> 14) & 0x7F,
				 (iValue >>  7) & 0x7F,
				 iValue & 0x7F ];
	setDeviceProperty(paramId, data);
}

export function setIndexedParameterValue(paramId, value) {
	var data = [ (value >> 21) & 0x7F,
				 (value >> 14) & 0x7F,
				 (value >>  7) & 0x7F,
				 value & 0x7F ];
	setDeviceProperty(paramId, data);
}

export function addProgramSubscriber(obj) {
	// Only one subscriber is supported, but
	// keep naming consistent with properties.
	programSubscriber = obj;
}

export function removeProgramSubscriber(obj) {
	if (programSubscriber == obj) {
		programSubscriber = null;
	}
}

export function addPropertySubscriber(propId, obj) {
	// only one subscriber is allowed for each property
	if (propId != undefined &&
		obj != undefined &&
		propertyValueSubscribers[propId] == undefined) {
		propertyValueSubscribers[propId] = obj;

		// update the control
		getPropertyValue(propId);
	}
}

export function removePropertySubscriber(propId, obj) {
	delete propertyValueSubscribers[propId];
}

export function addControlChangeSubscriber(ccNum, obj) {
	// only one subscriber is allowed for each property
	if (ccNum != undefined &&
		obj != undefined &&
		midiCCSubscribers[ccNum] == undefined) {
		midiCCSubscribers[ccNum] = obj;
	}
}

export function removeControlChangeSubscriber(ccNum, obj) {
	delete midiCCSubscribers[ccNum];
}

export function sendControlChange(ccNum, value) {
	var message = [0xB0 + midiChannel, ccNum, value];

	if (deviceOutput) {
		deviceOutput.send(message);
	}
}

export function sendProgramChange(progNum) {
	var message = [0xC0 + midiChannel, progNum];

	if (logToConsole) {
		console.log('Load: ' + progNum);
	}

	if (deviceOutput && progNum < 128) {
		deviceOutput.send(message);

		updateAllPropertyValues();
	}
}

export function saveProgram(progNum) {
	if (logToConsole) {
		console.log('Save: ' + progNum);	
	}

	setDeviceProperty(PROPERTY_SAVE_PRESET, [progNum]);
}
